/**
 * CAD Metric Snapping & Coordinate Transformation Logic (Ticket 04).
 * Standards:
 * - 1 Raised floor tile = 600 mm × 600 mm = 0.6 m × 0.6 m
 * - At 100 Pixels Per Meter (PPM), 1 tile = 60 px.
 * - Sub-tile resolution = 300 mm = 30 px.
 */

export const PPM = 100
export const TILE_PX = 60
export const SUB_TILE_PX = 30

/**
 * Snaps a pixel coordinate to the raised floor tile grid (default 60px = 600mm).
 */
export function snapToGrid(px: number, stepPx: number = TILE_PX): number {
  return Math.round(px / stepPx) * stepPx
}

/**
 * Snaps a pixel coordinate to half-tile resolution (30px = 300mm).
 */
export function snapToSubGrid(px: number, stepPx: number = SUB_TILE_PX): number {
  return Math.round(px / stepPx) * stepPx
}

/**
 * Converts pixels to physical meters (rounded to 2 decimal places).
 */
export function pxToMeters(px: number, ppm: number = PPM): number {
  return Number((px / ppm).toFixed(2))
}

/**
 * Converts physical meters to pixels.
 */
export function metersToPx(meters: number, ppm: number = PPM): number {
  return Math.round(meters * ppm)
}

/**
 * Converts pixels to raised floor tile index (0-based or 1-based offset).
 */
export function pxToTiles(px: number, tilePx: number = TILE_PX): number {
  return Math.round(px / tilePx)
}

/**
 * Converts tile count to pixels.
 */
export function tilesToPx(tiles: number, tilePx: number = TILE_PX): number {
  return Math.round(tiles * tilePx)
}

export interface RectLike {
  id: string
  x: number
  y: number
  width: number
  height: number
  rotation?: number
}

export interface SnapResult {
  x: number
  y: number
  snapType: 'baying' | 'grid'
  snappedToId?: string
}

/**
 * Calculates magnetic Baying Snap (rack-to-rack side-by-side or end-to-end alignment).
 * If within snapThresholdPx of an adjacent rack edge, snaps flush with zero gap.
 * Otherwise, falls back to snapping to the raised floor grid (60px).
 */
export function calculateBayingSnap(
  dragged: RectLike,
  otherRacks: RectLike[],
  snapThresholdPx: number = 15,
  gridStepPx: number = TILE_PX
): SnapResult {
  let closestDist = Infinity
  let bestSnap: SnapResult | null = null

  const draggedRot = Math.round((dragged.rotation || 0) / 90) % 2 !== 0
  const draggedStepAlongX = draggedRot ? dragged.height : dragged.width
  const draggedStepAlongY = draggedRot ? dragged.width : dragged.height

  for (const other of otherRacks) {
    if (other.id === dragged.id) continue

    const otherRot = Math.round((other.rotation || 0) / 90) % 2 !== 0
    const stepAlongX = otherRot ? other.height : other.width
    const stepAlongY = otherRot ? other.width : other.height

    // 1. Horizontal baying: dragged is immediately to the right of other
    const rightEdgeDist = Math.abs(dragged.x - (other.x + stepAlongX))
    const yDiffH = Math.abs(dragged.y - other.y)
    if (rightEdgeDist < snapThresholdPx && yDiffH < snapThresholdPx * 2) {
      const totalDist = rightEdgeDist + yDiffH
      if (totalDist < closestDist) {
        closestDist = totalDist
        bestSnap = {
          x: other.x + stepAlongX,
          y: other.y,
          snapType: 'baying',
          snappedToId: other.id,
        }
      }
    }

    // 2. Horizontal baying: dragged is immediately to the left of other
    const leftEdgeDist = Math.abs(dragged.x + draggedStepAlongX - other.x)
    if (leftEdgeDist < snapThresholdPx && yDiffH < snapThresholdPx * 2) {
      const totalDist = leftEdgeDist + yDiffH
      if (totalDist < closestDist) {
        closestDist = totalDist
        bestSnap = {
          x: other.x - draggedStepAlongX,
          y: other.y,
          snapType: 'baying',
          snappedToId: other.id,
        }
      }
    }

    // 3. Vertical baying: dragged is immediately below other
    const bottomEdgeDist = Math.abs(dragged.y - (other.y + stepAlongY))
    const xDiffV = Math.abs(dragged.x - other.x)
    if (bottomEdgeDist < snapThresholdPx && xDiffV < snapThresholdPx * 2) {
      const totalDist = bottomEdgeDist + xDiffV
      if (totalDist < closestDist) {
        closestDist = totalDist
        bestSnap = {
          x: other.x,
          y: other.y + stepAlongY,
          snapType: 'baying',
          snappedToId: other.id,
        }
      }
    }

    // 4. Vertical baying: dragged is immediately above other
    const topEdgeDist = Math.abs(dragged.y + draggedStepAlongY - other.y)
    if (topEdgeDist < snapThresholdPx && xDiffV < snapThresholdPx * 2) {
      const totalDist = topEdgeDist + xDiffV
      if (totalDist < closestDist) {
        closestDist = totalDist
        bestSnap = {
          x: other.x,
          y: other.y - draggedStepAlongY,
          snapType: 'baying',
          snappedToId: other.id,
        }
      }
    }
  }

  if (bestSnap) {
    return bestSnap
  }

  // Fallback: Grid snap
  return {
    x: snapToGrid(dragged.x, gridStepPx),
    y: snapToGrid(dragged.y, gridStepPx),
    snapType: 'grid',
  }
}
